import {
	IconButton,
	Stack,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { debounce } from "lodash-es";
import { Minus, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	INCREMENTAL_COUNTER_HOLD,
	INCREMENTAL_COUNTER_MAX,
	INCREMENTAL_COUNTER_STEP,
} from "web_myplann/Shared/constants";
import { useHoldRepeat } from "web_myplann/Shared/Hooks/useHoldRepeat";
import { parseCounterInput } from "web_myplann/Shared/Utils/parseCounterInput";

type Props = {
	defaultValue: number;
	setValue: (value: number) => void;
	disabled?: boolean;
};

export const IncrementalCounter = ({
	defaultValue,
	setValue,
	disabled,
}: Props) => {
	const theme = useTheme();
	const { t } = useResource();
	const [localValue, setLocalValue] = useState(defaultValue);
	const [editing, setEditing] = useState(false);

	const lastSetValueRef = useRef<number>(defaultValue);

	const { pressHandlers } = useHoldRepeat(INCREMENTAL_COUNTER_HOLD);

	const debouncedSetValue = useMemo(() => debounce(setValue, 200), [setValue]);

	useEffect(() => {
		debouncedSetValue(localValue);
		lastSetValueRef.current = localValue;
		return () => {
			debouncedSetValue.cancel();
		};
	}, [localValue, debouncedSetValue]);

	useEffect(() => {
		if (defaultValue !== lastSetValueRef.current) {
			setLocalValue(defaultValue);
			lastSetValueRef.current = defaultValue;
		}
	}, [defaultValue]);

	const nextLocalValue = useCallback((prev: number, delta: number) => {
		const next = Math.max(0, Math.min(INCREMENTAL_COUNTER_MAX, prev + delta));
		return (
			Math.floor(next / INCREMENTAL_COUNTER_STEP) * INCREMENTAL_COUNTER_STEP
		);
	}, []);

	const step = (delta: number) =>
		setLocalValue((prev) => nextLocalValue(prev, delta));

	const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setLocalValue(parseCounterInput(event.target.value));
	};

	return (
		<Stack
			direction="row"
			alignItems="center"
			justifyContent="space-between"
			p={1.2}
			gap={0.4}
		>
			<IconButton
				{...pressHandlers(() => step(-INCREMENTAL_COUNTER_STEP))}
				disabled={localValue <= 0 || disabled}
				sx={{
					border: `2px solid ${
						disabled
							? theme.palette.deprecated.grey[500]
							: theme.palette.primary.main
					}`,
					p: 1.6,
				}}
			>
				<Minus
					size={theme.iconSize.sm}
					color={
						disabled
							? theme.palette.deprecated.grey[500]
							: theme.palette.primary.main
					}
				/>
			</IconButton>

			{editing ? (
				<TextField
					disabled={disabled}
					autoFocus
					value={localValue === 0 ? "" : localValue.toLocaleString("cs-CZ")}
					onChange={handleFieldChange}
					onBlur={() => setEditing(false)}
					variant="standard"
					slotProps={{
						input: {
							disableUnderline: true,
							sx: { "& input": { p: 0, m: 0, height: "1.5em" } },
						},
					}}
					inputProps={{
						inputMode: "numeric",
						pattern: "[0-9]*",
						style: { fontSize: 40, fontWeight: 700, textAlign: "right" },
					}}
					sx={{ marginInline: 1 }}
				/>
			) : (
				<Stack
					direction="row"
					alignItems="flex-end"
					onClick={() => !disabled && setEditing(true)}
					sx={{ cursor: disabled ? "default" : "pointer" }}
				>
					<Typography
						fontSize={40}
						fontWeight={700}
						color={theme.palette.deprecated.grey[900]}
					>
						{localValue.toLocaleString("cs-CZ")}
					</Typography>
					<Stack pb={1} pl={1}>
						<Typography
							fontSize={16}
							fontWeight={700}
							color={theme.palette.deprecated.grey[900]}
						>
							{t(Resources.Common.CurrencySign)}
						</Typography>
					</Stack>
				</Stack>
			)}

			<IconButton
				{...pressHandlers(() => step(INCREMENTAL_COUNTER_STEP))}
				disabled={localValue >= INCREMENTAL_COUNTER_MAX || disabled}
				sx={{
					border: `2px solid ${
						disabled
							? theme.palette.deprecated.grey[500]
							: theme.palette.primary.main
					}`,
					p: 1.6,
				}}
			>
				<Plus
					size={theme.iconSize.sm}
					color={
						disabled
							? theme.palette.deprecated.grey[500]
							: theme.palette.primary.main
					}
				/>
			</IconButton>
		</Stack>
	);
};
